import React, { useState, useEffect } from 'react';
import AdvancedFormulaManager from './AdvancedFormulaManager.jsx';
import formulaEngine from './utils/FormulaEngine';

const SalaryStructureForm = ({ isOpen, onClose, onSubmit, savedComponents, loading, editingStructure, organizationalLevels = {}, statutoryRates = {} }) => {
  const [formData, setFormData] = useState({
    name: '',
    salaryType: 'gross', // 'gross' or 'ctc'
    components: [], // Array of components with their individual settings
    currentStep: 1, // 1: Structure details, 2: Component settings, 3: Salary Simulator, 4: Assignment
    effectiveMonth: '', // Effective month for editing saved structures
    assignment: {
      branch: [],
      department: [],
      designation: [],
      grade: [],
      subdepartment: [],
      category: [],
      employmentType: []
    }
  });

  const [showFormulaManager, setShowFormulaManager] = useState(false);
  const [currentComponentForFormula, setCurrentComponentForFormula] = useState(null);
  const [showOrgLevelsDropdown, setShowOrgLevelsDropdown] = useState(false);
  const [showEmploymentTypesDropdown, setShowEmploymentTypesDropdown] = useState(false);
  const [showEmployeeDropdown, setShowEmployeeDropdown] = useState(false);
  const [employeeSearchQuery, setEmployeeSearchQuery] = useState('');
  const [duplicateWarning, setDuplicateWarning] = useState(null);
  const [simulatorValidated, setSimulatorValidated] = useState(false);
  const [simulatorCtcAmount, setSimulatorCtcAmount] = useState('');
  const [simulatorCtcType, setSimulatorCtcType] = useState('monthly'); // 'monthly' | 'annual'
  const [simulatorMinWages, setSimulatorMinWages] = useState(14050);
  const [simulatorConveyance, setSimulatorConveyance] = useState(1600);
  const [simulatorPtState, setSimulatorPtState] = useState('Karnataka');
  const [simulatorUseExcelBreakup, setSimulatorUseExcelBreakup] = useState(true); // true = Excel-style Basic/HRA/Conveyance/Personal
  const [simulatorApplicability, setSimulatorApplicability] = useState({
    pfApplicable: true,
    esiApplicable: true,
    ptApplicable: true,
    gratuityApplicable: true,
    pfRestrict: false // when true: PF (Employee) deduction capped at ₹1,800 max; else as defined
  });

  // Reset form when modal opens or when editing structure changes
  useEffect(() => {
    if (isOpen) {
      if (editingStructure) {
        setFormData({
          name: editingStructure.name,
          salaryType: editingStructure.salaryType || 'gross',
          components: editingStructure.components || [],
          currentStep: 1,
          effectiveMonth: editingStructure.effectiveMonth || '',
          assignment: editingStructure.assignment || {
            branch: [],
            department: [],
            designation: [],
            grade: [],
            subdepartment: [],
            category: [],
            employmentType: [],
            employees: []
          }
        });
      } else {
        setFormData({
          name: '',
          salaryType: 'gross',
          components: [],
          currentStep: 1,
          effectiveMonth: '',
          assignment: {
            branch: [],
            department: [],
            designation: [],
            grade: [],
            subdepartment: [],
            category: [],
            employmentType: [],
            employees: []
          }
        });
      }
    }
  }, [isOpen, editingStructure]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Check for effective month warnings
    if (name === 'effectiveMonth' && editingStructure) {
      const currentMonth = new Date().toISOString().slice(0, 7);
      const originalMonth = editingStructure.effectiveMonth;
      
      if (value === originalMonth) {
        setDuplicateWarning({
          type: 'effective-month-same',
          message: 'You are selecting the same effective month. This will not change the effective date but may affect salary processing.'
        });
      } else if (value < currentMonth) {
        setDuplicateWarning({
          type: 'effective-month-retrospective',
          message: 'Warning: Selecting a retrospective effective month will lead to processed salary changes from the selected month.'
        });
      } else {
        setDuplicateWarning(null);
      }
    }

    // Check for duplicate structure names and assignments
    if (name === 'name' || name === 'effectiveMonth') {
      checkForDuplicates();
    }
  };

  const checkForDuplicates = () => {
    // This would typically check against existing structures
    // For now, we'll simulate the check
    const existingStructures = [
      { name: 'Manager Structure', effectiveMonth: '2024-01', assignment: { department: ['hr'], designation: ['manager'] } },
      { name: 'Executive Structure', effectiveMonth: '2024-02', assignment: { department: ['it'], designation: ['executive'] } }
    ];

    const currentAssignment = JSON.stringify(formData.assignment);
    
    const duplicateName = existingStructures.find(structure => 
      structure.name.toLowerCase() === formData.name.toLowerCase() && 
      structure.effectiveMonth === formData.effectiveMonth &&
      editingStructure?.id !== structure.id
    );

    const duplicateAssignment = existingStructures.find(structure => 
      JSON.stringify(structure.assignment) === currentAssignment &&
      editingStructure?.id !== structure.id
    );

    if (duplicateName) {
      setDuplicateWarning({
        type: 'duplicate-name',
        message: `A salary structure with the same name "${formData.name}" and effective month "${formData.effectiveMonth}" already exists.`
      });
    } else if (duplicateAssignment) {
      setDuplicateWarning({
        type: 'duplicate-assignment',
        message: 'The selected organizational levels and employees are already assigned to another salary structure.'
      });
    } else {
      setDuplicateWarning(null);
    }
  };

  const handleComponentAdd = (componentId) => {
    const component = savedComponents.find(comp => comp.id === componentId);
    if (component && !formData.components.find(comp => comp.id === componentId)) {
      const newComponent = {
        id: component.id,
        name: component.name,
        categories: component.categories,
        cumulative: component.cumulative,
        calculationType: 'manual',
        calculationMethod: 'fixed',
        calculationBasis: 'payDays',
        pfApplicable: false,
        esiApplicable: false,
        gratuityApplicable: false,
        taxTreatment: 'taxable',
        selectedFormula: null,
        formulaResult: null,
        autoAdjustCTC: false
      };
      
      setFormData(prev => ({
        ...prev,
        components: [...prev.components, newComponent]
      }));
    }
  };

  const handleComponentRemove = (componentId) => {
    setFormData(prev => ({
      ...prev,
      components: prev.components.filter(comp => comp.id !== componentId)
    }));
  };

  const handleComponentSettingChange = (componentId, setting, value) => {
    setFormData(prev => ({
      ...prev,
      components: prev.components.map(comp => {
        if (comp.id === componentId) {
          const updatedComp = { ...comp, [setting]: value };
          
          // If calculation method is changed to "onetime", set calculation basis to "recurrent"
          if (setting === 'calculationMethod' && value === 'onetime') {
            updatedComp.calculationBasis = 'recurrent';
          }
          
          return updatedComp;
        }
        return comp;
      })
    }));
  };

  const handleFormulaSelect = (formula) => {
    if (currentComponentForFormula) {
      setFormData(prev => ({
        ...prev,
        components: prev.components.map(comp => 
          comp.id === currentComponentForFormula 
            ? { ...comp, selectedFormula: formula, formulaResult: null }
            : comp
        )
      }));
      setShowFormulaManager(false);
      setCurrentComponentForFormula(null);
    }
  };

  const openFormulaManager = (componentId) => {
    setCurrentComponentForFormula(componentId);
    setShowFormulaManager(true);
  };

  const calculateFormulaResult = (component) => {
    if (!component.selectedFormula) return null;

    try {
      // Use the formula engine for proper calculation
      const mockData = formulaEngine.generateMockData(savedComponents, statutoryRates);
      const calculation = formulaEngine.calculateFormula(
        component.selectedFormula.formula,
        mockData
      );

      return calculation.success ? calculation.result : null;
    } catch (error) {
      console.error('Formula calculation error:', error);
      return null;
    }
  };

  // Normalize statutory config from StatutoryRatesManager shape or flat keys (PF_RATE, MAX_PF_LIMIT, etc.)
  const getStatutoryConfig = () => {
    const r = statutoryRates || {};
    const pf = r.pf && typeof r.pf === 'object' ? r.pf : {
      employeeShare: r.PF_RATE != null ? r.PF_RATE : 12,
      employerShare: 12,
      ceiling: r.MAX_PF_LIMIT != null ? r.MAX_PF_LIMIT : 15000
    };
    const esi = r.esi && typeof r.esi === 'object' ? r.esi : {
      employeeShare: 0.75,
      employerShare: 3.25,
      ceiling: r.ESI_CEILING != null ? r.ESI_CEILING : 21000,
      cutoff: 21000
    };
    const gratuity = r.gratuity && typeof r.gratuity === 'object' ? r.gratuity : {
      rate: r.GRATUITY_RATE != null ? r.GRATUITY_RATE : 4.81
    };
    let ptSlabs = [];
    if (r.pt && r.pt.states && r.pt.states[simulatorPtState]) {
      ptSlabs = r.pt.states[simulatorPtState];
    } else if (r.pt && Array.isArray(r.pt.slabs)) {
      ptSlabs = r.pt.slabs;
    } else {
      ptSlabs = [
        { min: 0, max: 15000, rate: 200 },
        { min: 15001, max: 30000, rate: 250 },
        { min: 30001, max: 50000, rate: 300 },
        { min: 50001, max: 100000, rate: 400 },
        { min: 100001, max: Infinity, rate: 500 }
      ];
    }
    return { pf, esi, gratuity, ptSlabs };
  };

  const getPtFromSlabs = (gross, slabs) => {
    if (!slabs || !slabs.length) return 0;
    const slab = slabs.find(s => gross >= s.min && gross <= s.max);
    return slab ? slab.rate : 0;
  };

  const round = (v) => Math.round(Number(v) || 0);

  /**
   * Excel-style breakup (Breakup for Simulator.xlsx):
   * Basic = max(40% CTC, Min Wages), HRA = 40% Basic, Conveyance = fixed, Special Allowances = balance.
   * Total Employer = PF(13%) + ESI(3.25%) + Gratuity(4.81%/12); Gross = CTC - Total Employer.
   * All components rounded. PF Restriction: when checked, PF (Employee) max ₹1,800.
   */
  const getExcelStyleBreakup = () => {
    const rawCtc = (simulatorCtcAmount != null && simulatorCtcAmount !== '' ? parseFloat(String(simulatorCtcAmount).replace(/,/g, '')) : null) || 50000;
    const monthlyCtc = round(simulatorCtcType === 'annual' ? rawCtc / 12 : rawCtc);
    const config = getStatutoryConfig();
    const minWages = Number(simulatorMinWages) || 14050;
    const conveyance = round(Number(simulatorConveyance) || 0);

    const { pf, esi, gratuity, ptSlabs } = config;
    const pfCeiling = pf.ceiling || 15000;
    const esiCeiling = esi.ceiling || esi.cutoff || 21000;
    const pfEmployerPct = (pf.employerShare || 12) + (pf.adminCharges || 0) + (pf.edliRate || 0.5) || 13;

    // 1) Basic = max(40% of CTC, Minimum Wages) — rounded
    const basic = round(Math.max(monthlyCtc * 0.4, minWages));
    // 2) HRA = 40% of Basic — rounded
    const hra = round(basic * 0.4);
    // 3) Conveyance fixed
    // 4) PF Employer & Gratuity (rounded)
    const pfEmployer = round(simulatorApplicability.pfApplicable ? Math.min(basic, pfCeiling) * (pfEmployerPct / 100) : 0);
    const gratuityAmt = round(simulatorApplicability.gratuityApplicable ? (basic * (gratuity.rate / 100)) / 12 : 0);
    // 5) Total Employer when ESI applicable
    let totalEmployer;
    const pfPlusGr = pfEmployer + gratuityAmt;
    if (simulatorApplicability.esiApplicable) {
      const totalEIfEsiFull = (pfPlusGr + (esi.employerShare / 100) * monthlyCtc) / (1 + esi.employerShare / 100);
      const grossEst = monthlyCtc - totalEIfEsiFull;
      if (grossEst <= esiCeiling) {
        totalEmployer = totalEIfEsiFull;
      } else {
        totalEmployer = pfPlusGr + (esi.employerShare / 100) * esiCeiling;
      }
    } else {
      totalEmployer = pfPlusGr;
    }
    totalEmployer = round(totalEmployer);
    const esiEmployer = round(simulatorApplicability.esiApplicable ? Math.max(0, totalEmployer - pfEmployer - gratuityAmt) : 0);
    // 6) Special Allowances (balance) = CTC - Total Employer - Basic - HRA - Conveyance
    const specialAllowances = round(Math.max(0, monthlyCtc - totalEmployer - basic - hra - conveyance));
    // 7) Gross = Basic + HRA + Conveyance + Special Allowances
    const gross = round(basic + hra + conveyance + specialAllowances);
    // 8) Employee deductions — PF Restriction: when checked, cap PF (Employee) at 1800
    let pfEmployee = simulatorApplicability.pfApplicable ? Math.min(basic, pfCeiling) * (pf.employeeShare / 100) : 0;
    if (simulatorApplicability.pfRestrict && simulatorApplicability.pfApplicable) {
      pfEmployee = Math.min(pfEmployee, 1800);
    }
    pfEmployee = round(pfEmployee);
    const esiEmployee = round(simulatorApplicability.esiApplicable ? Math.min(gross, esiCeiling) * (esi.employeeShare / 100) : 0);
    const pt = round(simulatorApplicability.ptApplicable ? getPtFromSlabs(gross, ptSlabs) : 0);
    const totalDeductions = round(pfEmployee + esiEmployee + pt);
    const netPay = round(gross - totalDeductions);

    const totalCtcCheck = round(gross + totalEmployer);
    const ctcMatch = Math.abs(totalCtcCheck - monthlyCtc) < 1;

    return {
      monthlyCtc,
      annualCtc: round(monthlyCtc * 12),
      basic,
      hra,
      conveyance,
      personal: specialAllowances, // alias for Special Allowances
      specialAllowances,
      gross,
      pfEmployer,
      esiEmployer,
      gratuity: gratuityAmt,
      totalEmployer,
      pfEmployee,
      esiEmployee,
      pt,
      totalDeductions,
      netPay,
      ctcMatch,
      totalCtcCheck
    };
  };

  const isStatutoryComponent = (component, type) => {
    const n = (component.name || '').toLowerCase();
    if (type === 'pf') return n.includes('pf') || n.includes('provident');
    if (type === 'esi') return n.includes('esi');
    if (type === 'pt') return n.includes('professional tax') || n === 'pt';
    if (type === 'gratuity') return n.includes('gratuity');
    return false;
  };

  const isEmployerShareComponent = (component) => {
    const n = (component.name || '').toLowerCase();
    return n.includes('employer') || (n.includes('gratuity') && !n.includes('employee'));
  };

  // Salary Simulator: dependency order, balance component, statutory from StatutoryRatesManager, per-component applicability
  const getSimulatorContextAndAmounts = (ctcValue) => {
    const rawCtc = (ctcValue != null && ctcValue !== '' ? parseFloat(ctcValue) : null) || 50000;
    const monthlyCtc = simulatorCtcType === 'annual' ? rawCtc / 12 : rawCtc;
    const config = getStatutoryConfig();
    const context = { CTC: monthlyCtc, ...(statutoryRates || {}) };
    const amountsByComponentId = {};

    // 1) Non-balance components in order (formula or fixed)
    formData.components.forEach((component) => {
      if (component.autoAdjustCTC) {
        amountsByComponentId[component.id] = 0;
        return;
      }
      let amount = 0;
      if (component.calculationType === 'formula' && component.selectedFormula) {
        const calculation = formulaEngine.calculateFormula(component.selectedFormula.formula, context);
        amount = calculation.success ? (calculation.result || 0) : 0;
      } else {
        amount = component.formulaResult != null ? Number(component.formulaResult) : 0;
      }
      amountsByComponentId[component.id] = amount;
      context[component.name] = amount;
    });

    // 2) Bases from per-component applicability (only earning components that are applicable)
    let pfBase = 0, gratuityBase = 0;
    formData.components.forEach((component) => {
      if (!component.categories?.earning) return;
      const amt = amountsByComponentId[component.id] || 0;
      if (component.pfApplicable) pfBase += amt;
      if (component.gratuityApplicable) gratuityBase += amt;
    });
    let grossBeforeBalance = 0;
    formData.components.forEach((c) => {
      if (c.categories?.earning) grossBeforeBalance += amountsByComponentId[c.id] || 0;
      if (c.categories?.benefit) grossBeforeBalance += amountsByComponentId[c.id] || 0;
      if (c.categories?.reimbursement) grossBeforeBalance += amountsByComponentId[c.id] || 0;
    });
    const esiBase = grossBeforeBalance; // gross used for ESI

    // 3) Statutory employer amounts (from StatutoryRatesManager rates)
    const pfCeiling = config.pf.ceiling || 15000;
    const pfEmployerAmount = simulatorApplicability.pfApplicable
      ? Math.min(pfBase, pfCeiling) * (config.pf.employerShare / 100)
      : 0;
    const esiCeiling = config.esi.ceiling || config.esi.cutoff || 21000;
    const esiEmployerAmount = simulatorApplicability.esiApplicable
      ? Math.min(esiBase, esiCeiling) * (config.esi.employerShare / 100)
      : 0;
    const gratuityAmount = simulatorApplicability.gratuityApplicable
      ? (gratuityBase * (config.gratuity.rate / 100)) / 12
      : 0;
    const statutoryEmployerTotal = pfEmployerAmount + esiEmployerAmount + gratuityAmount;

    // 4) Employer from configured components (by name)
    let employerFromComponents = 0;
    formData.components.forEach((c) => {
      if (isEmployerShareComponent(c)) employerFromComponents += amountsByComponentId[c.id] || 0;
    });

    // 5) Balance component = CTC - (Total Earnings) - (Employer from components) - (Statutory employer)
    const balanceComponent = formData.components.find((c) => c.autoAdjustCTC && c.categories?.earning);
    if (balanceComponent) {
      const totalEarningsSoFar = Object.keys(amountsByComponentId).reduce((sum, id) => {
        const comp = formData.components.find(c => c.id === id);
        if (comp && (comp.categories?.earning || comp.categories?.benefit || comp.categories?.reimbursement)) {
          return sum + (amountsByComponentId[id] || 0);
        }
        return sum;
      }, 0);
      const balance = monthlyCtc - totalEarningsSoFar - employerFromComponents - statutoryEmployerTotal;
      const balAmt = Math.max(0, balance);
      amountsByComponentId[balanceComponent.id] = balAmt;
      context[balanceComponent.name] = balAmt;
    }

    return {
      context,
      amountsByComponentId,
      monthlyCtc,
      config,
      statutoryEmployerUsedForBalance: {
        pfEmployer: pfEmployerAmount,
        esiEmployer: esiEmployerAmount,
        gratuity: gratuityAmount
      }
    };
  };

  const getSimulatorTotals = () => {
    const { amountsByComponentId, monthlyCtc, config, statutoryEmployerUsedForBalance } = getSimulatorContextAndAmounts(simulatorCtcAmount);
    const hasBalanceComponent = formData.components.some((c) => c.autoAdjustCTC && c.categories?.earning);
    let pfBase = 0, gratuityBase = 0, gross = 0;
    formData.components.forEach((c) => {
      const amt = amountsByComponentId[c.id] || 0;
      if (c.categories?.earning) {
        gross += amt;
        if (c.pfApplicable) pfBase += amt;
        if (c.gratuityApplicable) gratuityBase += amt;
      }
      if (c.categories?.benefit) gross += amt;
      if (c.categories?.reimbursement) gross += amt;
    });
    const esiBase = gross;
    const pfCeiling = config.pf.ceiling || 15000;
    const esiCeiling = config.esi.ceiling || config.esi.cutoff || 21000;

    let pfEmployerAmount, esiEmployerAmount, gratuityAmount;
    if (hasBalanceComponent && statutoryEmployerUsedForBalance) {
      pfEmployerAmount = statutoryEmployerUsedForBalance.pfEmployer;
      esiEmployerAmount = statutoryEmployerUsedForBalance.esiEmployer;
      gratuityAmount = statutoryEmployerUsedForBalance.gratuity;
    } else {
      pfEmployerAmount = simulatorApplicability.pfApplicable ? Math.min(pfBase, pfCeiling) * (config.pf.employerShare / 100) : 0;
      esiEmployerAmount = simulatorApplicability.esiApplicable ? Math.min(esiBase, esiCeiling) * (config.esi.employerShare / 100) : 0;
      gratuityAmount = simulatorApplicability.gratuityApplicable ? (gratuityBase * (config.gratuity.rate / 100)) / 12 : 0;
    }

    // Statutory employee deductions (from rates). PF Restriction: when checked, cap PF (Employee) at 1800.
    let pfEmployeeAmount = simulatorApplicability.pfApplicable ? Math.min(pfBase, pfCeiling) * (config.pf.employeeShare / 100) : 0;
    if (simulatorApplicability.pfRestrict && simulatorApplicability.pfApplicable) {
      pfEmployeeAmount = Math.min(pfEmployeeAmount, 1800);
    }
    pfEmployeeAmount = round(pfEmployeeAmount);
    const esiEmployeeAmount = round(simulatorApplicability.esiApplicable ? Math.min(esiBase, esiCeiling) * (config.esi.employeeShare / 100) : 0);
    const ptAmount = round(simulatorApplicability.ptApplicable ? getPtFromSlabs(gross, config.ptSlabs) : 0);

    const statutoryDeductionsList = [
      ...(simulatorApplicability.pfApplicable ? [{ name: 'PF (Employee)', amount: pfEmployeeAmount }] : []),
      ...(simulatorApplicability.esiApplicable ? [{ name: 'ESI (Employee)', amount: esiEmployeeAmount }] : []),
      ...(simulatorApplicability.ptApplicable ? [{ name: 'Professional Tax (PT)', amount: ptAmount }] : [])
    ];
    const statutoryEmployerList = [
      ...(simulatorApplicability.pfApplicable ? [{ name: 'PF (Employer)', amount: round(pfEmployerAmount) }] : []),
      ...(simulatorApplicability.esiApplicable ? [{ name: 'ESI (Employer)', amount: round(esiEmployerAmount) }] : []),
      ...(simulatorApplicability.gratuityApplicable ? [{ name: 'Gratuity', amount: round(gratuityAmount) }] : [])
    ];

    const totals = { earnings: 0, deductions: 0, benefits: 0, reimbursements: 0, gross: 0, net: 0, employerShares: 0 };
    const earningsList = [];
    const deductionsList = [];
    const employerList = [];
    const benefitsList = [];
    const reimbursementsList = [];

    formData.components.forEach((component) => {
      let amount = round(amountsByComponentId[component.id] || 0);
      const isEmployer = isEmployerShareComponent(component);
      if (component.categories?.earning) {
        totals.earnings += amount;
        earningsList.push({ name: component.name, amount });
      } else if (component.categories?.deduction) {
        if (isEmployer) {
          totals.employerShares += amount;
          employerList.push({ name: component.name, amount });
        } else {
          totals.deductions += amount;
          deductionsList.push({ name: component.name, amount });
        }
      } else if (component.categories?.benefit) {
        if (isEmployer) {
          totals.employerShares += amount;
          employerList.push({ name: component.name, amount });
        } else {
          totals.benefits += amount;
          benefitsList.push({ name: component.name, amount });
        }
      } else if (component.categories?.reimbursement) {
        totals.reimbursements += amount;
        reimbursementsList.push({ name: component.name, amount });
      }
    });

    totals.gross = round(totals.earnings + totals.benefits + totals.reimbursements);
    const totalStatutoryDeductions = statutoryDeductionsList.reduce((s, i) => s + i.amount, 0);
    const totalStatutoryEmployer = statutoryEmployerList.reduce((s, i) => s + i.amount, 0);
    totals.deductions = round(totals.deductions + totalStatutoryDeductions);
    totals.employerShares = round(totals.employerShares + totalStatutoryEmployer);
    totals.net = round(totals.gross - totals.deductions);
    totals.monthlyCtc = monthlyCtc;
    totals.annualCtc = round(monthlyCtc * 12);

    return {
      amountsByComponentId,
      totals,
      earningsList,
      deductionsList,
      employerList,
      benefitsList,
      reimbursementsList,
      statutoryDeductionsList,
      statutoryEmployerList
    };
  };

  const calculateStructureTotals = () => {
    return formulaEngine.calculateStructureTotals(formData.components);
  };

  const handleAssignmentChange = (category, value) => {
    setFormData(prev => ({
      ...prev,
      assignment: {
        ...prev.assignment,
        [category]: value
      }
    }));
  };

  const nextStep = () => {
    if (formData.currentStep === 1) {
      if (!formData.name.trim()) {
        alert('Please enter a structure name');
        return;
      }
      if (!formData.salaryType) {
        alert('Please select a salary structure type');
        return;
      }
    }
    if (formData.currentStep === 2 && formData.components.length === 0) {
      alert('Please add at least one component');
      return;
    }
    if (formData.currentStep === 3 && !simulatorValidated) {
      alert('Please validate the structure in Salary Simulator before proceeding to Assign Structure.');
      return;
    }
    setFormData(prev => ({ ...prev, currentStep: prev.currentStep + 1 }));
  };

  const prevStep = () => {
    if (formData.currentStep === 3) setSimulatorValidated(false);
    setFormData(prev => ({ ...prev, currentStep: prev.currentStep - 1 }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const handleReset = () => {
    setSimulatorValidated(false);
    setSimulatorCtcAmount('');
    setSimulatorMinWages(14050);
    setSimulatorConveyance(1600);
    setFormData({
      name: '',
      salaryType: 'gross',
      components: [],
      currentStep: 1,
      assignment: {
        branch: [],
        department: [],
        designation: [],
        grade: [],
        subdepartment: [],
        category: [],
        employmentType: []
      }
    });
  };

  const getCategoryLabels = (categories) => {
    const labels = [];
    if (categories.earning) labels.push('Earning');
    if (categories.deduction) labels.push('Deduction');
    if (categories.benefit) labels.push('Benefit');
    if (categories.reimbursement) labels.push('Reimbursement');
    return labels.join(', ');
  };

  const getCategoryBadgeColor = (categories) => {
    if (categories.earning) return 'bg-green-100 text-green-800';
    if (categories.deduction) return 'bg-red-100 text-red-800';
    if (categories.benefit) return 'rh-accent-soft';
    if (categories.reimbursement) return 'bg-purple-100 text-purple-800';
    return 'bg-gray-100 text-gray-800';
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-[95vw] max-w-7xl mx-4 max-h-[95vh] flex flex-col">
        {/* Modal Header - Fixed */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              {editingStructure ? 'Edit Salary Structure' : 'Create New Salary Structure'}
            </h2>
            <div className="flex items-center mt-2 space-x-4 flex-wrap gap-y-2">
              <div className={`flex items-center ${formData.currentStep >= 1 ? 'text-redhalo-navy' : 'text-gray-400'}`}>
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${
                  formData.currentStep >= 1 ? 'rh-btn-primary' : 'bg-gray-200 text-gray-600'
                }`}>
                  1
                </div>
                <span className="ml-2 text-sm">Structure Details</span>
              </div>
              <div className={`flex items-center ${formData.currentStep >= 2 ? 'text-redhalo-navy' : 'text-gray-400'}`}>
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${
                  formData.currentStep >= 2 ? 'rh-btn-primary' : 'bg-gray-200 text-gray-600'
                }`}>
                  2
                </div>
                <span className="ml-2 text-sm">Component Settings</span>
              </div>
              <div className={`flex items-center ${formData.currentStep >= 3 ? 'text-redhalo-navy' : 'text-gray-400'}`}>
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${
                  formData.currentStep >= 3 ? 'rh-btn-primary' : 'bg-gray-200 text-gray-600'
                }`}>
                  3
                </div>
                <span className="ml-2 text-sm">Salary Simulator</span>
              </div>
              <div className={`flex items-center ${formData.currentStep >= 4 ? 'text-redhalo-navy' : 'text-gray-400'}`}>
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${
                  formData.currentStep >= 4 ? 'rh-btn-primary' : 'bg-gray-200 text-gray-600'
                }`}>
                  4
                </div>
                <span className="ml-2 text-sm">Assign Structure</span>
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Modal Body - scrollable content with fixed footer */}
        <div className="flex flex-col flex-1 min-h-0">
        <div className="flex-1 min-h-0 overflow-y-auto p-6">
          {formData.currentStep === 1 && (
            <div className="space-y-6">
              {/* Structure Name */}
              <div>
                <label htmlFor="structure_name" className="block text-sm font-medium text-gray-700 mb-2">
                  Structure Name *
                </label>
                <input
                  type="text"
                  id="structure_name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-redhalo-red/35 focus:border-redhalo-red"
                  placeholder="Enter structure name"
                />
              </div>

              {/* Salary Type Toggle */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Type of Salary Structure *
                </label>
                <div className="flex items-center space-x-4">
                  <div className="flex items-center">
                    <input
                      type="radio"
                      id="salary_type_gross"
                      name="salaryType"
                      value="gross"
                      checked={formData.salaryType === 'gross'}
                      onChange={handleInputChange}
                      className="h-4 w-4 text-redhalo-navy focus:ring-redhalo-red/35 border-gray-300"
                    />
                    <label htmlFor="salary_type_gross" className="ml-2 text-sm text-gray-700">
                      Gross Salary
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="radio"
                      id="salary_type_ctc"
                      name="salaryType"
                      value="ctc"
                      checked={formData.salaryType === 'ctc'}
                      onChange={handleInputChange}
                      className="h-4 w-4 text-redhalo-navy focus:ring-redhalo-red/35 border-gray-300"
                    />
                    <label htmlFor="salary_type_ctc" className="ml-2 text-sm text-gray-700">
                      CTC (Cost to Company)
                    </label>
                  </div>
                </div>
                <div className="mt-2 text-xs text-gray-500">
                  {formData.salaryType === 'gross' ? (
                    <span>Gross Salary: Take-home salary before deductions</span>
                  ) : (
                    <span>CTC: Total cost to company including all benefits and deductions</span>
                  )}
                </div>
              </div>

              {/* Effective Month - Always show */}
              <div>
                <label htmlFor="effective_month" className="block text-sm font-medium text-gray-700 mb-2">
                  Effective Month *
                </label>
                <input
                  type="month"
                  id="effective_month"
                  name="effectiveMonth"
                  value={formData.effectiveMonth}
                  onChange={handleInputChange}
                  required
                  min={editingStructure ? new Date().toISOString().slice(0, 7) : ''}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-redhalo-red/35 focus:border-redhalo-red"
                />
                <p className="mt-1 text-xs text-gray-500">
                  {editingStructure 
                    ? 'Select the month from which this structure will be effective (cannot be retrospective)'
                    : 'Select the month from which this structure will be effective'
                  }
                </p>
                {editingStructure && (
                  <p className="mt-1 text-xs text-red-600">
                    ⚠️ Cannot select a month earlier than the current month when editing
                  </p>
                )}
                
                {/* Warning Messages */}
                {duplicateWarning && (
                  <div className={`mt-2 p-3 rounded-md border ${
                    duplicateWarning.type === 'effective-month-retrospective' ? 'bg-red-50 border-red-200' :
                    duplicateWarning.type === 'effective-month-same' ? 'bg-yellow-50 border-yellow-200' :
                    duplicateWarning.type === 'duplicate-name' ? 'bg-red-50 border-red-200' :
                    'bg-orange-50 border-orange-200'
                  }`}>
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <svg className={`h-5 w-5 ${
                          duplicateWarning.type === 'effective-month-retrospective' ? 'text-red-400' :
                          duplicateWarning.type === 'effective-month-same' ? 'text-yellow-400' :
                          'text-orange-400'
                        }`} viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <p className={`text-sm font-medium ${
                          duplicateWarning.type === 'effective-month-retrospective' ? 'text-red-800' :
                          duplicateWarning.type === 'effective-month-same' ? 'text-yellow-800' :
                          'text-orange-800'
                        }`}>
                          {duplicateWarning.message}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Available Components */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="block text-sm font-medium text-gray-700">
                    Available Components ({savedComponents.length})
                  </label>
                  <div className="flex items-center space-x-3">
                    {formData.components.length > 0 && (
                      <span className="text-sm text-redhalo-navy font-medium">
                        {formData.components.length} selected
                      </span>
                    )}
                    {savedComponents.length > 0 && (
                      <button
                        onClick={() => {
                          if (formData.components.length === savedComponents.length) {
                            // Deselect all
                            setFormData(prev => ({ ...prev, components: [] }));
                          } else {
                            // Select all
                            const allComponents = savedComponents.map(component => ({
                              ...component,
                              calculationType: 'manual',
                              calculationMethod: 'fixed',
                              calculationBasis: 'payDays',
                              pfApplicable: false,
                              esiApplicable: false,
                              gratuityApplicable: false,
                              taxTreatment: 'taxable',
                              autoAdjustCTC: false
                            }));
                            setFormData(prev => ({ ...prev, components: allComponents }));
                          }
                        }}
                        className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                          formData.components.length === savedComponents.length
                            ? 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                            : 'rh-btn-primary hover:opacity-95'
                        }`}
                      >
                        {formData.components.length === savedComponents.length ? 'Deselect All' : 'Select All'}
                      </button>
                    )}
                  </div>
                </div>
                {loading ? (
                  <div className="text-center py-4">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-redhalo-navy mx-auto"></div>
                    <p className="mt-2 text-sm text-gray-600">Loading components...</p>
                  </div>
                ) : (
                  <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-md bg-gray-50 p-4">
                    {savedComponents.length === 0 ? (
                      <div className="text-center py-8">
                        <div className="text-gray-400 mb-4">
                          <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                        </div>
                        <p className="text-sm text-gray-500 mb-2">No components available</p>
                        <p className="text-xs text-gray-400">Please create components first to build salary structures</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {savedComponents.map((component) => {
                          const isAdded = formData.components.find(comp => comp.id === component.id);
                          return (
                            <div
                              key={component.id}
                              className={`border rounded-lg p-3 transition-all duration-200 cursor-pointer ${
                                isAdded
                                  ? 'border-gray-300 bg-redhalo-mint/40 shadow-sm'
                                  : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-md'
                              }`}
                              onClick={() => !isAdded && handleComponentAdd(component.id)}
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex-1">
                                  <div className="flex items-center space-x-2 mb-2">
                                    <h4 className="text-sm font-medium text-gray-900">
                                      {component.name}
                                    </h4>
                                    {!component.active && (
                                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                        Inactive
                                      </span>
                                    )}
                                    {isAdded && (
                                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium rh-accent-soft">
                                        Selected
                                      </span>
                                    )}
                                  </div>
                                  
                                  <div className="flex items-center space-x-2">
                                    <div className="flex flex-wrap gap-1">
                                      {component.categories.earning && (
                                        <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                          Earning
                                        </span>
                                      )}
                                      {component.categories.deduction && (
                                        <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                          Deduction
                                        </span>
                                      )}
                                      {component.categories.benefit && (
                                        <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium rh-accent-soft">
                                          Benefit
                                        </span>
                                      )}
                                      {component.categories.reimbursement && (
                                        <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                          Reimbursement
                                        </span>
                                      )}
                                    </div>
                                    
                                    <span className="text-xs text-gray-500">
                                      Cumulative: {component.cumulative === 'yes' ? 'Yes' : 'No'}
                                    </span>
                                  </div>
                                </div>
                                
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (isAdded) {
                                      handleComponentRemove(component.id);
                                    } else {
                                      handleComponentAdd(component.id);
                                    }
                                  }}
                                  className={`ml-3 px-2 py-1 text-xs font-medium rounded-md transition-all duration-200 ${
                                    isAdded
                                      ? 'bg-red-100 text-red-700 hover:bg-red-200 hover:text-red-800'
                                      : 'rh-btn-primary hover:opacity-95'
                                  }`}
                                >
                                  {isAdded ? (
                                    <>
                                      <svg className="h-3 w-3 mr-1 inline" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                      </svg>
                                      Remove
                                    </>
                                  ) : (
                                    <>
                                      <svg className="h-3 w-3 mr-1 inline" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                      </svg>
                                      Add
                                    </>
                                  )}
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {formData.currentStep === 2 && (
            <div>
              <div className="sticky top-0 z-10 -mx-6 px-6 py-4 mb-4 bg-white border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">Configure Component Settings</h3>
                  <span className="text-sm text-gray-500">{formData.components.length} components added</span>
                </div>
              </div>

              <div className="space-y-4">
                {formData.components.map((component) => (
                  <div key={component.id} className="border rounded-lg p-4 bg-gray-50">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h4 className="text-base font-medium text-gray-900">{component.name}</h4>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {component.categories.earning && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              Earning
                            </span>
                          )}
                          {component.categories.deduction && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                              Deduction
                            </span>
                          )}
                          {component.categories.benefit && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium rh-accent-soft">
                              Benefit
                            </span>
                          )}
                          {component.categories.reimbursement && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                              Reimbursement
                            </span>
                          )}
                        </div>
                      </div>
                      <button
                        onClick={() => handleComponentRemove(component.id)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50 p-1 rounded-md transition-colors"
                      >
                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Calculation Type */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Calculation Type
                        </label>
                        <div className="flex space-x-4">
                          <div className="flex items-center">
                            <input
                              type="radio"
                              name={`calc_type_${component.id}`}
                              value="manual"
                              checked={component.calculationType === 'manual'}
                              onChange={(e) => handleComponentSettingChange(component.id, 'calculationType', e.target.value)}
                              className="h-4 w-4 text-redhalo-navy focus:ring-redhalo-red/35 border-gray-300"
                            />
                            <label className="ml-2 text-sm text-gray-700">Manual</label>
                          </div>
                          <div className="flex items-center">
                            <input
                              type="radio"
                              name={`calc_type_${component.id}`}
                              value="formula"
                              checked={component.calculationType === 'formula'}
                              onChange={(e) => handleComponentSettingChange(component.id, 'calculationType', e.target.value)}
                              className="h-4 w-4 text-redhalo-navy focus:ring-redhalo-red/35 border-gray-300"
                            />
                            <label className="ml-2 text-sm text-gray-700">Formula</label>
                          </div>
                        </div>
                        
                        {/* Formula Selection */}
                        {component.calculationType === 'formula' && (
                          <div className="mt-3 p-3 bg-redhalo-mint/40 border border-redhalo-teal/50 rounded-md">
                            <div className="flex items-center justify-between mb-2">
                              <label className="text-sm font-medium text-redhalo-navy">
                                Selected Formula
                              </label>
                              <button
                                type="button"
                                onClick={() => openFormulaManager(component.id)}
                                className="text-xs px-2 py-1 rh-btn-primary rounded hover:opacity-95 transition-colors"
                              >
                                {component.selectedFormula ? 'Change Formula' : 'Select Formula'}
                              </button>
                            </div>
                            
                            {component.selectedFormula ? (
                              <div className="space-y-2">
                                <div className="flex items-center space-x-2">
                                  <span className="text-sm font-medium text-redhalo-navy-dark">
                                    {component.selectedFormula.name}
                                  </span>
                                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                    component.selectedFormula.category === 'earning' ? 'bg-green-100 text-green-800' :
                                    component.selectedFormula.category === 'deduction' ? 'bg-red-100 text-red-800' :
                                    component.selectedFormula.category === 'benefit' ? 'rh-accent-soft' :
                                    'bg-purple-100 text-purple-800'
                                  }`}>
                                    {component.selectedFormula.category}
                                  </span>
                                </div>
                                
                                <div className="bg-white rounded p-2 border">
                                  <code className="text-xs text-gray-800 font-mono">
                                    {component.selectedFormula.formula}
                                  </code>
                                </div>
                                
                                {component.selectedFormula.description && (
                                  <p className="text-xs text-redhalo-navy">
                                    {component.selectedFormula.description}
                                  </p>
                                )}
                                
                                <div className="flex items-center space-x-3">
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const result = calculateFormulaResult(component);
                                      handleComponentSettingChange(component.id, 'formulaResult', result);
                                    }}
                                    className="text-xs px-2 py-1 bg-green-600 text-white rounded hover:bg-green-700 transition-colors"
                                  >
                                    Calculate
                                  </button>
                                  
                                  {component.formulaResult !== null && (
                                    <span className="text-sm font-medium text-green-800">
                                      Result: ₹{component.formulaResult?.toLocaleString() || 'Error'}
                                    </span>
                                  )}
                                </div>
                              </div>
                            ) : (
                              <p className="text-xs text-redhalo-navy">
                                No formula selected. Click "Select Formula" to choose a calculation formula.
                              </p>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Calculation Method */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Calculation Method
                        </label>
                        <div className="flex space-x-4">
                          <div className="flex items-center">
                            <input
                              type="radio"
                              name={`calc_method_${component.id}`}
                              value="fixed"
                              checked={component.calculationMethod === 'fixed'}
                              onChange={(e) => handleComponentSettingChange(component.id, 'calculationMethod', e.target.value)}
                              className="h-4 w-4 text-redhalo-navy focus:ring-redhalo-red/35 border-gray-300"
                            />
                            <label className="ml-2 text-sm text-gray-700">Fixed</label>
                          </div>
                          <div className="flex items-center">
                            <input
                              type="radio"
                              name={`calc_method_${component.id}`}
                              value="onetime"
                              checked={component.calculationMethod === 'onetime'}
                              onChange={(e) => handleComponentSettingChange(component.id, 'calculationMethod', e.target.value)}
                              className="h-4 w-4 text-redhalo-navy focus:ring-redhalo-red/35 border-gray-300"
                            />
                            <label className="ml-2 text-sm text-gray-700">One Time</label>
                          </div>
                          <div className="flex items-center">
                            <input
                              type="radio"
                              name={`calc_method_${component.id}`}
                              value="variable"
                              checked={component.calculationMethod === 'variable'}
                              onChange={(e) => handleComponentSettingChange(component.id, 'calculationMethod', e.target.value)}
                              className="h-4 w-4 text-redhalo-navy focus:ring-redhalo-red/35 border-gray-300"
                            />
                            <label className="ml-2 text-sm text-gray-700">Variable</label>
                          </div>
                        </div>
                      </div>

                      {/* Calculation Basis */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Calculation Basis
                        </label>
                        <div className="flex space-x-4">
                          <div className="flex items-center">
                            <input
                              type="radio"
                              name={`calc_basis_${component.id}`}
                              value="payDays"
                              checked={component.calculationBasis === 'payDays'}
                              onChange={(e) => handleComponentSettingChange(component.id, 'calculationBasis', e.target.value)}
                              disabled={component.calculationMethod === 'onetime'}
                              className="h-4 w-4 text-redhalo-navy focus:ring-redhalo-red/35 border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                            />
                            <label className={`ml-2 text-sm ${component.calculationMethod === 'onetime' ? 'text-gray-400' : 'text-gray-700'}`}>
                              On Pay Days
                            </label>
                          </div>
                          <div className="flex items-center">
                            <input
                              type="radio"
                              name={`calc_basis_${component.id}`}
                              value="presentDays"
                              checked={component.calculationBasis === 'presentDays'}
                              onChange={(e) => handleComponentSettingChange(component.id, 'calculationBasis', e.target.value)}
                              disabled={component.calculationMethod === 'onetime'}
                              className="h-4 w-4 text-redhalo-navy focus:ring-redhalo-red/35 border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                            />
                            <label className={`ml-2 text-sm ${component.calculationMethod === 'onetime' ? 'text-gray-400' : 'text-gray-700'}`}>
                              On Present Days
                            </label>
                          </div>
                          <div className="flex items-center">
                            <input
                              type="radio"
                              name={`calc_basis_${component.id}`}
                              value="recurrent"
                              checked={component.calculationBasis === 'recurrent'}
                              onChange={(e) => handleComponentSettingChange(component.id, 'calculationBasis', e.target.value)}
                              className="h-4 w-4 text-redhalo-navy focus:ring-redhalo-red/35 border-gray-300"
                            />
                            <label className="ml-2 text-sm text-gray-700">Recurrent</label>
                          </div>
                        </div>
                        {component.calculationMethod === 'onetime' && (
                          <p className="mt-1 text-xs text-gray-500">
                            One-time calculation method automatically uses recurrent basis
                          </p>
                        )}
                      </div>

                      {/* Tax Treatment */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Tax Treatment {component.categories.deduction && <span className="text-red-500">*</span>}
                        </label>
                        {component.categories.deduction ? (
                          <select
                            value={component.taxTreatment || ''}
                            onChange={(e) => handleComponentSettingChange(component.id, 'taxTreatment', e.target.value)}
                            required
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-redhalo-red/35 focus:border-redhalo-red"
                          >
                            <option value="">Select Tax Treatment *</option>
                            <option value="deduction_head">Deduction Head</option>
                            <option value="section10">Section 10 - Exemptions</option>
                            <option value="section16">Section 16 - Standard Deduction</option>
                            <option value="section24">Section 24 - Interest on Home Loan</option>
                            <option value="section80C">Section 80C - Investments</option>
                            <option value="section80CCD">Section 80CCD - NPS Contribution</option>
                            <option value="section80CCD1">Section 80CCD(1) - NPS Self Contribution</option>
                            <option value="section80CCD1B">Section 80CCD(1B) - Additional NPS Contribution</option>
                            <option value="section80CCD2">Section 80CCD(2) - NPS Employer Contribution</option>
                            <option value="section80D">Section 80D - Medical Insurance</option>
                            <option value="section80E">Section 80E - Education Loan Interest</option>
                            <option value="section80G">Section 80G - Donations</option>
                            <option value="section80TTA">Section 80TTA - Interest on Savings</option>
                            <option value="section80TTB">Section 80TTB - Interest on Deposits</option>
                            <option value="section87A">Section 87A - Rebate</option>
                            <option value="nontaxable">Non-Taxable</option>
                          </select>
                        ) : (
                          <div className="flex space-x-4">
                            <div className="flex items-center">
                              <input
                                type="radio"
                                name={`tax_${component.id}`}
                                value="taxable"
                                checked={component.taxTreatment === 'taxable'}
                                onChange={(e) => handleComponentSettingChange(component.id, 'taxTreatment', e.target.value)}
                                className="h-4 w-4 text-redhalo-navy focus:ring-redhalo-red/35 border-gray-300"
                              />
                              <label className="ml-2 text-sm text-gray-700">Taxable</label>
                            </div>
                            <div className="flex items-center">
                              <input
                                type="radio"
                                name={`tax_${component.id}`}
                                value="nontaxable"
                                checked={component.taxTreatment === 'nontaxable'}
                                onChange={(e) => handleComponentSettingChange(component.id, 'taxTreatment', e.target.value)}
                                className="h-4 w-4 text-redhalo-navy focus:ring-redhalo-red/35 border-gray-300"
                              />
                              <label className="ml-2 text-sm text-gray-700">Non-Taxable</label>
                            </div>
                            <div className="flex items-center">
                              <input
                                type="radio"
                                name={`tax_${component.id}`}
                                value="onetime"
                                checked={component.taxTreatment === 'onetime'}
                                onChange={(e) => handleComponentSettingChange(component.id, 'taxTreatment', e.target.value)}
                                className="h-4 w-4 text-redhalo-navy focus:ring-redhalo-red/35 border-gray-300"
                              />
                              <label className="ml-2 text-sm text-gray-700">One Time</label>
                            </div>
                            <div className="flex items-center">
                              <input
                                type="radio"
                                name={`tax_${component.id}`}
                                value="partial"
                                checked={component.taxTreatment === 'partial'}
                                onChange={(e) => handleComponentSettingChange(component.id, 'taxTreatment', e.target.value)}
                                className="h-4 w-4 text-redhalo-navy focus:ring-redhalo-red/35 border-gray-300"
                              />
                              <label className="ml-2 text-sm text-gray-700">Partial</label>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Applicable Checkboxes - Only for non-deduction components */}
                    {!component.categories.deduction && (
                      <div className="mt-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Applicable
                        </label>
                        <div className="flex space-x-6">
                          <div className="flex items-center">
                            <input
                              type="checkbox"
                              checked={component.pfApplicable}
                              onChange={(e) => handleComponentSettingChange(component.id, 'pfApplicable', e.target.checked)}
                              className="h-4 w-4 text-redhalo-navy focus:ring-redhalo-red/35 border-gray-300 rounded"
                            />
                            <label className="ml-2 text-sm text-gray-700">PF Applicable</label>
                          </div>
                          <div className="flex items-center">
                            <input
                              type="checkbox"
                              checked={component.esiApplicable}
                              onChange={(e) => handleComponentSettingChange(component.id, 'esiApplicable', e.target.checked)}
                              className="h-4 w-4 text-redhalo-navy focus:ring-redhalo-red/35 border-gray-300 rounded"
                            />
                            <label className="ml-2 text-sm text-gray-700">ESI Applicable</label>
                          </div>
                          <div className="flex items-center">
                            <input
                              type="checkbox"
                              checked={component.gratuityApplicable}
                              onChange={(e) => handleComponentSettingChange(component.id, 'gratuityApplicable', e.target.checked)}
                              className="h-4 w-4 text-redhalo-navy focus:ring-redhalo-red/35 border-gray-300 rounded"
                            />
                            <label className="ml-2 text-sm text-gray-700">Gratuity Applicable</label>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Consider balance amount of CTC - Only for earning components when CTC is selected */}
                    {formData.salaryType === 'ctc' && component.categories.earning && (
                      <div className="mt-4">
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            id={`autoAdjust_${component.id}`}
                            checked={component.autoAdjustCTC || false}
                            onChange={(e) => handleComponentSettingChange(component.id, 'autoAdjustCTC', e.target.checked)}
                            className="h-4 w-4 text-redhalo-navy focus:ring-redhalo-red/35 border-gray-300 rounded"
                          />
                          <label htmlFor={`autoAdjust_${component.id}`} className="ml-2 text-sm text-gray-700">
                            Consider balance amount of CTC
                          </label>
                        </div>
                        <p className="mt-1 text-xs text-gray-500">
                          When checked, this component&apos;s value = CTC − (all other gross salary components + employer contributions). Use for e.g. Special Allowance to balance CTC.
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {formData.currentStep === 3 && (() => {
            const excelBreakup = getExcelStyleBreakup();
            const { amountsByComponentId, totals, earningsList, deductionsList, employerList, benefitsList, reimbursementsList, statutoryDeductionsList, statutoryEmployerList } = getSimulatorTotals();
            const hasComponentResults = formData.components.length > 0;
            const useExcel = simulatorUseExcelBreakup;
            return (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-900">Salary Structure Simulator</h3>
              <p className="text-sm text-gray-600">
                Standard breakup: Basic = max(40% CTC, Min Wages), HRA = 40% Basic, Conveyance fixed, Special Allowances = balance. Statutory PF/ESI/Gratuity/PT from rates. All amounts rounded. PF Restriction: when checked, PF (Employee) max ₹1,800.
              </p>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Left: Simulation Inputs (reference: SalaryStructureSimulator.jsx) */}
                <div className="space-y-6">
                  <h4 className="text-base font-semibold text-gray-900">Simulation Inputs</h4>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">CTC</label>
                    <div className="flex space-x-4 mb-2">
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="simulatorCtcType"
                          value="monthly"
                          checked={simulatorCtcType === 'monthly'}
                          onChange={() => setSimulatorCtcType('monthly')}
                          className="h-4 w-4 text-redhalo-navy focus:ring-redhalo-red/35 border-gray-300"
                        />
                        <span className="ml-2 text-sm text-gray-700">Monthly</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="simulatorCtcType"
                          value="annual"
                          checked={simulatorCtcType === 'annual'}
                          onChange={() => setSimulatorCtcType('annual')}
                          className="h-4 w-4 text-redhalo-navy focus:ring-redhalo-red/35 border-gray-300"
                        />
                        <span className="ml-2 text-sm text-gray-700">Annual</span>
                      </label>
                    </div>
                    <input
                      type="number"
                      min="0"
                      step="100"
                      value={simulatorCtcAmount}
                      onChange={(e) => setSimulatorCtcAmount(e.target.value)}
                      placeholder={simulatorCtcType === 'monthly' ? 'e.g. 50000' : 'e.g. 600000'}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-redhalo-red/35"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      {formData.salaryType === 'ctc' ? 'CTC' : 'Gross'} (₹/{simulatorCtcType === 'monthly' ? 'month' : 'year'}). Default 50,000/month if empty.
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Minimum Wages (₹/month)</label>
                    <input
                      type="number"
                      min="0"
                      step="100"
                      value={simulatorMinWages}
                      onChange={(e) => setSimulatorMinWages(e.target.value)}
                      placeholder="14050"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-redhalo-red/35"
                    />
                    <p className="mt-1 text-xs text-gray-500">Floor for Basic. Basic = max(40% of CTC, Minimum Wages).</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Conveyance (₹/month)</label>
                    <input
                      type="number"
                      min="0"
                      step="100"
                      value={simulatorConveyance}
                      onChange={(e) => setSimulatorConveyance(e.target.value)}
                      placeholder="1600 or 0"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-redhalo-red/35"
                    />
                    <p className="mt-1 text-xs text-gray-500">Fixed conveyance allowance. Use 0 for trainees if not applicable.</p>
                  </div>
                  {statutoryRates?.pt?.states && Object.keys(statutoryRates.pt.states).length > 0 && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">PT State</label>
                      <select
                        value={simulatorPtState}
                        onChange={(e) => setSimulatorPtState(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-redhalo-red/35"
                      >
                        {Object.keys(statutoryRates.pt.states).map((state) => (
                          <option key={state} value={state}>{state}</option>
                        ))}
                      </select>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Statutory Applicabilities</label>
                    <div className="space-y-2">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={simulatorApplicability.pfApplicable}
                          onChange={(e) => setSimulatorApplicability(prev => ({ ...prev, pfApplicable: e.target.checked }))}
                          className="h-4 w-4 text-redhalo-navy focus:ring-redhalo-red/35 border-gray-300 rounded"
                        />
                        <span className="ml-2 text-sm text-gray-700">Provident Fund (PF)</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={simulatorApplicability.esiApplicable}
                          onChange={(e) => setSimulatorApplicability(prev => ({ ...prev, esiApplicable: e.target.checked }))}
                          className="h-4 w-4 text-redhalo-navy focus:ring-redhalo-red/35 border-gray-300 rounded"
                        />
                        <span className="ml-2 text-sm text-gray-700">Employee State Insurance (ESI)</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={simulatorApplicability.ptApplicable}
                          onChange={(e) => setSimulatorApplicability(prev => ({ ...prev, ptApplicable: e.target.checked }))}
                          className="h-4 w-4 text-redhalo-navy focus:ring-redhalo-red/35 border-gray-300 rounded"
                        />
                        <span className="ml-2 text-sm text-gray-700">Professional Tax (PT)</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={simulatorApplicability.gratuityApplicable}
                          onChange={(e) => setSimulatorApplicability(prev => ({ ...prev, gratuityApplicable: e.target.checked }))}
                          className="h-4 w-4 text-redhalo-navy focus:ring-redhalo-red/35 border-gray-300 rounded"
                        />
                        <span className="ml-2 text-sm text-gray-700">Gratuity</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={simulatorApplicability.pfRestrict || false}
                          onChange={(e) => setSimulatorApplicability(prev => ({ ...prev, pfRestrict: e.target.checked }))}
                          className="h-4 w-4 text-redhalo-navy focus:ring-redhalo-red/35 border-gray-300 rounded"
                        />
                        <span className="ml-2 text-sm text-gray-700">PF Restriction</span>
                      </label>
                      {simulatorApplicability.pfRestrict && (
                        <p className="text-xs text-gray-500 ml-6">PF (Employee) deduction capped at ₹1,800 max; when unchecked, as per defined rate.</p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={simulatorUseExcelBreakup}
                        onChange={(e) => setSimulatorUseExcelBreakup(e.target.checked)}
                        className="h-4 w-4 text-redhalo-navy focus:ring-redhalo-red/35 border-gray-300 rounded"
                      />
                      <span className="ml-2 text-sm text-gray-700">Standard breakup (Basic / HRA / Conveyance / Special Allowances)</span>
                    </label>
                  </div>
                  {!simulatorValidated ? (
                    <div className="flex items-center gap-4">
                      <button
                        type="button"
                        onClick={() => {
                          const ex = getExcelStyleBreakup();
                          if (useExcel && ex.netPay < 0) {
                            alert('Net pay is negative. Please adjust CTC or applicability.');
                            return;
                          }
                          if (!useExcel) {
                            const { totals: t } = getSimulatorTotals();
                            if (t.net < 0) {
                              alert('Net pay is negative. Please adjust components or applicability.');
                              return;
                            }
                          }
                          setSimulatorValidated(true);
                        }}
                        className="px-6 py-2 rh-btn-primary rounded-md hover:opacity-95 focus:outline-none focus:ring-2 focus:ring-redhalo-red/35"
                      >
                        Validate Structure
                      </button>
                      <span className="text-sm text-gray-500">Validate to proceed to Assign Structure</span>
                    </div>
                  ) : (
                    <p className="text-sm text-green-600 font-medium">Structure validated. You can proceed to Assign Structure.</p>
                  )}
                </div>

                {/* Right: Calculation Results - Excel-style or configured components */}
                <div className="space-y-6">
                  <h4 className="text-base font-semibold text-gray-900">Calculation Results</h4>

                  {useExcel ? (
                    <div className="space-y-4">
                      <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                        <h5 className="font-medium text-gray-900 mb-3">Salary Breakdown (A)</h5>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between"><span>Basic &amp; Dearness Allowances</span><span>₹{excelBreakup.basic.toLocaleString('en-IN')}</span></div>
                          <div className="flex justify-between"><span>House Rent Allowances</span><span>₹{excelBreakup.hra.toLocaleString('en-IN')}</span></div>
                          <div className="flex justify-between"><span>Conveyance</span><span>₹{excelBreakup.conveyance.toLocaleString('en-IN')}</span></div>
                          <div className="flex justify-between"><span>Special Allowances</span><span>₹{(excelBreakup.specialAllowances ?? excelBreakup.personal).toLocaleString('en-IN')}</span></div>
                          <div className="flex justify-between font-medium border-t border-gray-300 pt-2 mt-2"><span>Gross Salary</span><span>₹{excelBreakup.gross.toLocaleString('en-IN')}</span></div>
                        </div>
                      </div>
                      <div className="bg-redhalo-mint/40 p-4 rounded-lg border border-redhalo-teal/50">
                        <h5 className="font-medium text-gray-900 mb-3">Employer Contributions (B)</h5>
                        <div className="space-y-2 text-sm">
                          {simulatorApplicability.pfApplicable && <div className="flex justify-between"><span>PF (Employer)</span><span>₹{excelBreakup.pfEmployer.toLocaleString('en-IN')}</span></div>}
                          {simulatorApplicability.esiApplicable && <div className="flex justify-between"><span>ESI (Employer)</span><span>₹{excelBreakup.esiEmployer.toLocaleString('en-IN')}</span></div>}
                          {simulatorApplicability.gratuityApplicable && <div className="flex justify-between"><span>Gratuity</span><span>₹{excelBreakup.gratuity.toLocaleString('en-IN')}</span></div>}
                          <div className="flex justify-between font-medium border-t border-gray-300 pt-2 mt-2 text-redhalo-navy-dark"><span>Total Employer Shares (B)</span><span>₹{excelBreakup.totalEmployer.toLocaleString('en-IN')}</span></div>
                        </div>
                      </div>
                      <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                        <h5 className="font-medium text-gray-900 mb-3">Deductions (C)</h5>
                        <div className="space-y-2 text-sm">
                          {simulatorApplicability.pfApplicable && <div className="flex justify-between"><span>PF (Employee)</span><span>₹{excelBreakup.pfEmployee.toLocaleString('en-IN')}</span></div>}
                          {simulatorApplicability.esiApplicable && <div className="flex justify-between"><span>ESI (Employee)</span><span>₹{excelBreakup.esiEmployee.toLocaleString('en-IN')}</span></div>}
                          {simulatorApplicability.ptApplicable && <div className="flex justify-between"><span>Professional Tax (PT)</span><span>₹{excelBreakup.pt.toLocaleString('en-IN')}</span></div>}
                          <div className="flex justify-between font-medium border-t border-red-300 pt-2 mt-2 text-red-700"><span>Total Deductions (C)</span><span>₹{excelBreakup.totalDeductions.toLocaleString('en-IN')}</span></div>
                        </div>
                      </div>
                      <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                        <h5 className="font-medium text-gray-900 mb-3">Summary</h5>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between font-medium"><span>Net Pay (A − C)</span><span className="text-green-700">₹{excelBreakup.netPay.toLocaleString('en-IN')}</span></div>
                          <div className="flex justify-between text-gray-600"><span>Total CTC (A+B) Monthly</span><span>₹{excelBreakup.monthlyCtc.toLocaleString('en-IN')}</span></div>
                          <div className="flex justify-between text-gray-600"><span>Total CTC (A+B) Annual</span><span>₹{excelBreakup.annualCtc.toLocaleString('en-IN')}</span></div>
                          <div className={`flex justify-between font-medium pt-2 border-t ${excelBreakup.ctcMatch ? 'text-green-700 border-green-300' : 'text-amber-700 border-amber-300'}`}>
                            <span>CTC Checker</span>
                            <span>{excelBreakup.ctcMatch ? 'Correct' : 'Wrong'}</span>
                          </div>
                          {!excelBreakup.ctcMatch && <p className="text-xs text-amber-600">Gross + Employer = ₹{excelBreakup.totalCtcCheck.toLocaleString('en-IN')} (expected ₹{excelBreakup.monthlyCtc.toLocaleString('en-IN')})</p>}
                        </div>
                      </div>
                    </div>
                  ) : hasComponentResults ? (
                    <div className="space-y-4">
                      <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                        <h5 className="font-medium text-gray-900 mb-3">Salary Breakdown</h5>
                        <div className="space-y-2 text-sm">
                          {earningsList.map((item) => <div key={item.name} className="flex justify-between"><span>{item.name}</span><span>₹{item.amount.toLocaleString('en-IN')}</span></div>)}
                          {benefitsList.map((item) => <div key={item.name} className="flex justify-between"><span>{item.name}</span><span>₹{item.amount.toLocaleString('en-IN')}</span></div>)}
                          {reimbursementsList.map((item) => <div key={item.name} className="flex justify-between"><span>{item.name}</span><span>₹{item.amount.toLocaleString('en-IN')}</span></div>)}
                          <div className="flex justify-between font-medium border-t border-gray-300 pt-2 mt-2"><span>Gross Salary</span><span>₹{totals.gross.toLocaleString('en-IN')}</span></div>
                        </div>
                      </div>
                      <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                        <h5 className="font-medium text-gray-900 mb-3">Deductions (Employee)</h5>
                        <div className="space-y-2 text-sm">
                          {deductionsList.map((item) => <div key={item.name} className="flex justify-between"><span>{item.name}</span><span>₹{item.amount.toLocaleString('en-IN')}</span></div>)}
                          {statutoryDeductionsList && statutoryDeductionsList.map((item) => <div key={item.name} className="flex justify-between"><span>{item.name}</span><span>₹{item.amount.toLocaleString('en-IN')}</span></div>)}
                          <div className="flex justify-between font-medium border-t border-red-300 pt-2 mt-2 text-red-700"><span>Total Deductions</span><span>₹{totals.deductions.toLocaleString('en-IN')}</span></div>
                        </div>
                      </div>
                      <div className="bg-redhalo-mint/40 p-4 rounded-lg border border-redhalo-teal/50">
                        <h5 className="font-medium text-gray-900 mb-3">Employer Contributions</h5>
                        <div className="space-y-2 text-sm">
                          {employerList.map((item) => <div key={item.name} className="flex justify-between"><span>{item.name}</span><span>₹{item.amount.toLocaleString('en-IN')}</span></div>)}
                          {statutoryEmployerList && statutoryEmployerList.map((item) => <div key={item.name} className="flex justify-between"><span>{item.name}</span><span>₹{item.amount.toLocaleString('en-IN')}</span></div>)}
                          <div className="flex justify-between font-medium border-t border-gray-300 pt-2 mt-2 text-redhalo-navy-dark"><span>Total Employer Share</span><span>₹{(totals.employerShares || 0).toLocaleString('en-IN')}</span></div>
                        </div>
                      </div>
                      <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                        <h5 className="font-medium text-gray-900 mb-3">Summary</h5>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between font-medium"><span>Net Salary (Take Home)</span><span className="text-green-700">₹{totals.net.toLocaleString('en-IN')}</span></div>
                          {totals.monthlyCtc != null && <div className="flex justify-between text-gray-600"><span>Total CTC (Monthly)</span><span>₹{(totals.monthlyCtc || 0).toLocaleString('en-IN')}</span></div>}
                          {totals.annualCtc != null && <div className="flex justify-between text-gray-600"><span>Total CTC (Annual)</span><span>₹{(totals.annualCtc || 0).toLocaleString('en-IN')}</span></div>}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-gray-50 p-8 rounded-lg border border-gray-200 text-center">
                      <p className="text-gray-500">Enable &quot;Standard breakup&quot; or configure components in Step 2 and enter CTC to see results.</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Full component preview: Monthly and Annual breakup */}
              {hasComponentResults && (
                <div className="rounded-lg border border-gray-200 overflow-hidden">
                  <h4 className="text-sm font-medium text-gray-700 mb-2 px-2">Preview of configured components (Monthly & Annual)</h4>
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Component</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Monthly (₹)</th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Annual (₹)</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {formData.components.map((component) => {
                        let amount = amountsByComponentId[component.id] || 0;
                        if (isStatutoryComponent(component, 'pf') && !simulatorApplicability.pfApplicable) amount = 0;
                        if (isStatutoryComponent(component, 'esi') && !simulatorApplicability.esiApplicable) amount = 0;
                        if (isStatutoryComponent(component, 'pt') && !simulatorApplicability.ptApplicable) amount = 0;
                        if (isStatutoryComponent(component, 'gratuity') && !simulatorApplicability.gratuityApplicable) amount = 0;
                        amount = round(amount);
                        const cat = [];
                        if (component.categories?.earning) cat.push('Earning');
                        if (component.categories?.deduction) cat.push('Deduction');
                        if (component.categories?.benefit) cat.push('Benefit');
                        if (component.categories?.reimbursement) cat.push('Reimbursement');
                        return (
                          <tr key={component.id}>
                            <td className="px-4 py-2 text-sm text-gray-900">{component.name}</td>
                            <td className="px-4 py-2 text-sm text-gray-600">{cat.join(', ') || '-'}</td>
                            <td className="px-4 py-2 text-sm text-right font-medium">{amount.toLocaleString('en-IN')}</td>
                            <td className="px-4 py-2 text-sm text-right text-gray-600">{(amount * 12).toLocaleString('en-IN')}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            );
          })()}

          {formData.currentStep === 4 && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-900">Assign Structure</h3>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Left Side - Organizational Levels Selection */}
                <div className="space-y-6">
                  {/* Organizational Levels - Single Line Dropdown */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Organizational Levels</label>
                    <div className="relative">
                      <button
                        type="button"
                        onClick={() => setShowOrgLevelsDropdown(!showOrgLevelsDropdown)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-redhalo-red/35 focus:border-redhalo-red text-left flex items-center justify-between"
                      >
                        <span className="text-gray-700">
                          {(() => {
                            const totalSelected = Object.values(formData.assignment).reduce((sum, arr) => sum + arr.length, 0);
                            return totalSelected > 0 ? `${totalSelected} levels selected` : 'Select organizational levels';
                          })()}
                        </span>
                        <svg className={`h-4 w-4 text-gray-400 transition-transform ${showOrgLevelsDropdown ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                      
                      {showOrgLevelsDropdown && (
                        <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-80 overflow-y-auto">
                          <div className="p-4 space-y-4">
                            {/* Branch */}
                            <div className="border-b border-gray-200 pb-3">
                              <h5 className="text-sm font-medium text-gray-900 mb-2">Branch</h5>
                              <div className="grid grid-cols-2 gap-2">
                                {['main-branch', 'north-branch', 'south-branch', 'east-branch', 'west-branch'].map(branch => (
                                  <label key={branch} className="flex items-center">
                                    <input
                                      type="checkbox"
                                      checked={formData.assignment.branch.includes(branch)}
                                      onChange={(e) => {
                                        const current = formData.assignment.branch;
                                        const updated = e.target.checked 
                                          ? [...current, branch]
                                          : current.filter(b => b !== branch);
                                        handleAssignmentChange('branch', updated);
                                      }}
                                      className="h-4 w-4 text-redhalo-navy focus:ring-redhalo-red/35 border-gray-300 rounded"
                                    />
                                    <span className="ml-2 text-sm text-gray-700 capitalize">{branch.replace('-', ' ')}</span>
                                  </label>
                                ))}
                              </div>
                            </div>
                            
                            {/* Department */}
                            <div className="border-b border-gray-200 pb-3">
                              <h5 className="text-sm font-medium text-gray-900 mb-2">Department</h5>
                              <div className="grid grid-cols-2 gap-2">
                                {[
                                  { value: 'hr', label: 'Human Resources' },
                                  { value: 'finance', label: 'Finance' },
                                  { value: 'it', label: 'Information Technology' },
                                  { value: 'marketing', label: 'Marketing' },
                                  { value: 'sales', label: 'Sales' },
                                  { value: 'operations', label: 'Operations' }
                                ].map(dept => (
                                  <label key={dept.value} className="flex items-center">
                                    <input
                                      type="checkbox"
                                      checked={formData.assignment.department.includes(dept.value)}
                                      onChange={(e) => {
                                        const current = formData.assignment.department;
                                        const updated = e.target.checked 
                                          ? [...current, dept.value]
                                          : current.filter(d => d !== dept.value);
                                        handleAssignmentChange('department', updated);
                                      }}
                                      className="h-4 w-4 text-redhalo-navy focus:ring-redhalo-red/35 border-gray-300 rounded"
                                    />
                                    <span className="ml-2 text-sm text-gray-700">{dept.label}</span>
                                  </label>
                                ))}
                              </div>
                            </div>
                            
                            {/* Designation */}
                            <div className="border-b border-gray-200 pb-3">
                              <h5 className="text-sm font-medium text-gray-900 mb-2">Designation</h5>
                              <div className="grid grid-cols-2 gap-2">
                                {[
                                  { value: 'manager', label: 'Manager' },
                                  { value: 'senior-manager', label: 'Senior Manager' },
                                  { value: 'director', label: 'Director' },
                                  { value: 'executive', label: 'Executive' },
                                  { value: 'senior-executive', label: 'Senior Executive' },
                                  { value: 'associate', label: 'Associate' }
                                ].map(desig => (
                                  <label key={desig.value} className="flex items-center">
                                    <input
                                      type="checkbox"
                                      checked={formData.assignment.designation.includes(desig.value)}
                                      onChange={(e) => {
                                        const current = formData.assignment.designation;
                                        const updated = e.target.checked 
                                          ? [...current, desig.value]
                                          : current.filter(d => d !== desig.value);
                                        handleAssignmentChange('designation', updated);
                                      }}
                                      className="h-4 w-4 text-redhalo-navy focus:ring-redhalo-red/35 border-gray-300 rounded"
                                    />
                                    <span className="ml-2 text-sm text-gray-700">{desig.label}</span>
                                  </label>
                                ))}
                              </div>
                            </div>
                            
                            {/* Grade */}
                            <div className="border-b border-gray-200 pb-3">
                              <h5 className="text-sm font-medium text-gray-900 mb-2">Grade</h5>
                              <div className="grid grid-cols-2 gap-2">
                                {['grade-a', 'grade-b', 'grade-c', 'grade-d', 'grade-e'].map(grade => (
                                  <label key={grade} className="flex items-center">
                                    <input
                                      type="checkbox"
                                      checked={formData.assignment.grade.includes(grade)}
                                      onChange={(e) => {
                                        const current = formData.assignment.grade;
                                        const updated = e.target.checked 
                                          ? [...current, grade]
                                          : current.filter(g => g !== grade);
                                        handleAssignmentChange('grade', updated);
                                      }}
                                      className="h-4 w-4 text-redhalo-navy focus:ring-redhalo-red/35 border-gray-300 rounded"
                                    />
                                    <span className="ml-2 text-sm text-gray-700 capitalize">{grade.replace('-', ' ')}</span>
                                  </label>
                                ))}
                              </div>
                            </div>
                            
                            {/* Sub Department */}
                            <div className="border-b border-gray-200 pb-3">
                              <h5 className="text-sm font-medium text-gray-900 mb-2">Sub Department</h5>
                              <div className="grid grid-cols-2 gap-2">
                                {[
                                  { value: 'recruitment', label: 'Recruitment' },
                                  { value: 'payroll', label: 'Payroll' },
                                  { value: 'accounts', label: 'Accounts' },
                                  { value: 'development', label: 'Development' },
                                  { value: 'support', label: 'Support' }
                                ].map(subdept => (
                                  <label key={subdept.value} className="flex items-center">
                                    <input
                                      type="checkbox"
                                      checked={formData.assignment.subdepartment.includes(subdept.value)}
                                      onChange={(e) => {
                                        const current = formData.assignment.subdepartment;
                                        const updated = e.target.checked 
                                          ? [...current, subdept.value]
                                          : current.filter(s => s !== subdept.value);
                                        handleAssignmentChange('subdepartment', updated);
                                      }}
                                      className="h-4 w-4 text-redhalo-navy focus:ring-redhalo-red/35 border-gray-300 rounded"
                                    />
                                    <span className="ml-2 text-sm text-gray-700">{subdept.label}</span>
                                  </label>
                                ))}
                              </div>
                            </div>
                            
                            {/* Employment Status */}
                            <div className="border-b border-gray-200 pb-3">
                              <h5 className="text-sm font-medium text-gray-900 mb-2">Employment Status</h5>
                              <div className="grid grid-cols-2 gap-2">
                                {[
                                  { value: 'permanent', label: 'Permanent' },
                                  { value: 'temporary', label: 'Temporary' },
                                  { value: 'probation', label: 'Probation' },
                                  { value: 'intern', label: 'Intern' }
                                ].map(category => (
                                  <label key={category.value} className="flex items-center">
                                    <input
                                      type="checkbox"
                                      checked={formData.assignment.category.includes(category.value)}
                                      onChange={(e) => {
                                        const current = formData.assignment.category;
                                        const updated = e.target.checked 
                                          ? [...current, category.value]
                                          : current.filter(c => c !== category.value);
                                        handleAssignmentChange('category', updated);
                                      }}
                                      className="h-4 w-4 text-redhalo-navy focus:ring-redhalo-red/35 border-gray-300 rounded"
                                    />
                                    <span className="ml-2 text-sm text-gray-700">{category.label}</span>
                                  </label>
                                ))}
                              </div>
                            </div>
                            
                            {/* Employment Types */}
                            <div>
                              <h5 className="text-sm font-medium text-gray-900 mb-2">Employment Types</h5>
                              <div className="grid grid-cols-2 gap-2">
                                {[
                                  { value: 'fulltime', label: 'Full Time' },
                                  { value: 'parttime', label: 'Part Time' },
                                  { value: 'consultant', label: 'Consultant' },
                                  { value: 'contract', label: 'Contract' }
                                ].map(type => (
                                  <label key={type.value} className="flex items-center">
                                    <input
                                      type="checkbox"
                                      checked={formData.assignment.employmentType.includes(type.value)}
                                      onChange={(e) => {
                                        const current = formData.assignment.employmentType;
                                        const updated = e.target.checked 
                                          ? [...current, type.value]
                                          : current.filter(t => t !== type.value);
                                        handleAssignmentChange('employmentType', updated);
                                      }}
                                      className="h-4 w-4 text-redhalo-navy focus:ring-redhalo-red/35 border-gray-300 rounded"
                                    />
                                    <span className="ml-2 text-sm text-gray-700">{type.label}</span>
                                  </label>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>


                  {/* Employee Assignment - Single Line Dropdown with Search */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Employee Assignment</label>
                    <div className="relative">
                      <button
                        type="button"
                        onClick={() => setShowEmployeeDropdown(!showEmployeeDropdown)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-redhalo-red/35 focus:border-redhalo-red text-left flex items-center justify-between"
                      >
                        <span className="text-gray-700">
                          {formData.assignment.employees.length > 0 
                            ? `${formData.assignment.employees.length} employees selected` 
                            : 'Select employees'
                          }
                        </span>
                        <svg className={`h-4 w-4 text-gray-400 transition-transform ${showEmployeeDropdown ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                      
                      {showEmployeeDropdown && (
                        <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-hidden">
                          {/* Search Bar */}
                          <div className="p-3 border-b border-gray-200">
                            <input
                              type="text"
                              placeholder="Search employees..."
                              value={employeeSearchQuery}
                              onChange={(e) => setEmployeeSearchQuery(e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-redhalo-red/35 focus:border-redhalo-red text-sm"
                            />
                          </div>
                          
                          {/* Employee List */}
                          <div className="max-h-48 overflow-y-auto">
                            <div className="p-3 space-y-2">
                              {(() => {
                                const employees = [
                                  { id: 'emp001', name: 'John Doe', department: 'IT', designation: 'Manager' },
                                  { id: 'emp002', name: 'Jane Smith', department: 'HR', designation: 'Senior Manager' },
                                  { id: 'emp003', name: 'Mike Johnson', department: 'Finance', designation: 'Executive' },
                                  { id: 'emp004', name: 'Sarah Wilson', department: 'Marketing', designation: 'Manager' },
                                  { id: 'emp005', name: 'David Brown', department: 'Sales', designation: 'Senior Executive' },
                                  { id: 'emp006', name: 'Lisa Anderson', department: 'IT', designation: 'Developer' },
                                  { id: 'emp007', name: 'Robert Taylor', department: 'Finance', designation: 'Analyst' },
                                  { id: 'emp008', name: 'Emily Davis', department: 'HR', designation: 'Recruiter' },
                                  { id: 'emp009', name: 'Michael Wilson', department: 'Marketing', designation: 'Specialist' },
                                  { id: 'emp010', name: 'Jennifer Lee', department: 'Sales', designation: 'Representative' }
                                ];
                                
                                const filteredEmployees = employees.filter(emp => 
                                  emp.name.toLowerCase().includes(employeeSearchQuery.toLowerCase()) ||
                                  emp.department.toLowerCase().includes(employeeSearchQuery.toLowerCase()) ||
                                  emp.designation.toLowerCase().includes(employeeSearchQuery.toLowerCase())
                                );
                                
                                return filteredEmployees.map((employee) => (
                                  <label key={employee.id} className="flex items-center p-2 hover:bg-gray-50 rounded cursor-pointer">
                                    <input
                                      type="checkbox"
                                      checked={formData.assignment.employees.includes(employee.id)}
                                      onChange={(e) => {
                                        const current = formData.assignment.employees;
                                        const updated = e.target.checked 
                                          ? [...current, employee.id]
                                          : current.filter(id => id !== employee.id);
                                        handleAssignmentChange('employees', updated);
                                      }}
                                      className="h-4 w-4 text-redhalo-navy focus:ring-redhalo-red/35 border-gray-300 rounded"
                                    />
                                    <div className="ml-3 flex-1">
                                      <span className="text-sm font-medium text-gray-900">{employee.name}</span>
                                      <span className="text-xs text-gray-500 ml-2">({employee.department} - {employee.designation})</span>
                                    </div>
                                  </label>
                                ));
                              })()}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Right Side - Assignment Summary */}
                <div className="space-y-6">
                  <div className="p-4 bg-redhalo-mint/40 rounded-lg border border-redhalo-teal/50">
                    <h4 className="text-base font-medium text-redhalo-navy mb-3">Assignment Summary</h4>
                    <div className="space-y-4 text-sm">
                      {/* Organizational Levels */}
                      <div>
                        <span className="font-medium text-redhalo-navy-dark">Organizational Levels:</span>
                        <div className="mt-2 space-y-2">
                          {formData.assignment.branch.length > 0 && (
                            <div>
                              <span className="text-redhalo-navy font-medium">Branches:</span>
                              <div className="flex flex-wrap gap-1 mt-1">
                                {formData.assignment.branch.map(branch => (
                                  <span key={branch} className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium rh-accent-soft">
                                    {branch.replace('-', ' ')}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                          {formData.assignment.department.length > 0 && (
                            <div>
                              <span className="text-redhalo-navy font-medium">Departments:</span>
                              <div className="flex flex-wrap gap-1 mt-1">
                                {formData.assignment.department.map(dept => (
                                  <span key={dept} className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium rh-accent-soft">
                                    {dept === 'hr' ? 'Human Resources' : 
                                     dept === 'it' ? 'Information Technology' : 
                                     dept.charAt(0).toUpperCase() + dept.slice(1)}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                          {formData.assignment.designation.length > 0 && (
                            <div>
                              <span className="text-redhalo-navy font-medium">Designations:</span>
                              <div className="flex flex-wrap gap-1 mt-1">
                                {formData.assignment.designation.map(desig => (
                                  <span key={desig} className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium rh-accent-soft">
                                    {desig === 'senior-manager' ? 'Senior Manager' :
                                     desig === 'senior-executive' ? 'Senior Executive' :
                                     desig.charAt(0).toUpperCase() + desig.slice(1)}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                          {formData.assignment.grade.length > 0 && (
                            <div>
                              <span className="text-redhalo-navy font-medium">Grades:</span>
                              <div className="flex flex-wrap gap-1 mt-1">
                                {formData.assignment.grade.map(grade => (
                                  <span key={grade} className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium rh-accent-soft">
                                    {grade.replace('-', ' ').toUpperCase()}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                          {formData.assignment.subdepartment.length > 0 && (
                            <div>
                              <span className="text-redhalo-navy font-medium">Sub Departments:</span>
                              <div className="flex flex-wrap gap-1 mt-1">
                                {formData.assignment.subdepartment.map(subdept => (
                                  <span key={subdept} className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium rh-accent-soft">
                                    {subdept.charAt(0).toUpperCase() + subdept.slice(1)}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                          {formData.assignment.category.length > 0 && (
                            <div>
                              <span className="text-redhalo-navy font-medium">Employment Status:</span>
                              <div className="flex flex-wrap gap-1 mt-1">
                                {formData.assignment.category.map(category => (
                                  <span key={category} className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium rh-accent-soft">
                                    {category.charAt(0).toUpperCase() + category.slice(1)}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {/* Employment Types */}
                      {formData.assignment.employmentType.length > 0 && (
                        <div>
                          <span className="font-medium text-redhalo-navy-dark">Employment Types:</span>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {formData.assignment.employmentType.map(type => (
                              <span key={type} className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                {type === 'fulltime' ? 'Full Time' :
                                 type === 'parttime' ? 'Part Time' :
                                 type.charAt(0).toUpperCase() + type.slice(1)}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Selected Employees */}
                      {formData.assignment.employees.length > 0 && (
                        <div>
                          <span className="font-medium text-redhalo-navy-dark">Selected Employees:</span>
                          <div className="mt-2 space-y-1 max-h-32 overflow-y-auto">
                            {(() => {
                              const employees = [
                                { id: 'emp001', name: 'John Doe', department: 'IT', designation: 'Manager' },
                                { id: 'emp002', name: 'Jane Smith', department: 'HR', designation: 'Senior Manager' },
                                { id: 'emp003', name: 'Mike Johnson', department: 'Finance', designation: 'Executive' },
                                { id: 'emp004', name: 'Sarah Wilson', department: 'Marketing', designation: 'Manager' },
                                { id: 'emp005', name: 'David Brown', department: 'Sales', designation: 'Senior Executive' },
                                { id: 'emp006', name: 'Lisa Anderson', department: 'IT', designation: 'Developer' },
                                { id: 'emp007', name: 'Robert Taylor', department: 'Finance', designation: 'Analyst' },
                                { id: 'emp008', name: 'Emily Davis', department: 'HR', designation: 'Recruiter' },
                                { id: 'emp009', name: 'Michael Wilson', department: 'Marketing', designation: 'Specialist' },
                                { id: 'emp010', name: 'Jennifer Lee', department: 'Sales', designation: 'Representative' }
                              ];
                              
                              const selectedEmployees = employees.filter(emp => 
                                formData.assignment.employees.includes(emp.id)
                              );
                              
                              return selectedEmployees.map(employee => (
                                <div key={employee.id} className="flex items-center justify-between p-2 bg-white rounded border">
                                  <div>
                                    <span className="text-sm font-medium text-gray-900">{employee.name}</span>
                                    <span className="text-xs text-gray-500 ml-2">({employee.department} - {employee.designation})</span>
                                  </div>
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                    Selected
                                  </span>
                                </div>
                              ));
                            })()}
                          </div>
                        </div>
                      )}
                      
                      {/* No Selection Message */}
                      {Object.values(formData.assignment).every(arr => arr.length === 0) && (
                        <div className="text-center py-4">
                          <p className="text-redhalo-navy text-sm">No assignments selected yet</p>
                          <p className="text-redhalo-red text-xs mt-1">Select organizational levels, employment types, or specific employees above</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

            </div>
          )}

        </div>

          {/* Action Buttons - fixed at bottom of modal */}
          <div className="flex justify-between px-6 py-4 border-t border-gray-200 flex-shrink-0 bg-white">
            <div>
              {formData.currentStep > 1 && (
                <button
                  type="button"
                  onClick={prevStep}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-redhalo-red/35 focus:ring-offset-2 transition-colors"
                >
                  Previous
                </button>
              )}
            </div>
            
            <div className="flex space-x-3">
              <button
                type="button"
                onClick={handleReset}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-redhalo-red/35 focus:ring-offset-2 transition-colors"
              >
                Reset
              </button>
              
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-redhalo-red/35 focus:ring-offset-2 transition-colors"
              >
                Cancel
              </button>
              
              {formData.currentStep < 4 ? (
                <button
                  type="button"
                  onClick={nextStep}
                  className="rh-btn-primary px-4 py-2 rounded-md hover:opacity-95 focus:outline-none focus:ring-2 focus:ring-redhalo-red/35 focus:ring-offset-2 transition-colors"
                >
                  Next
                </button>
              ) : (
                <button
                  type="submit"
                  onClick={handleSubmit}
                  className="rh-btn-primary px-4 py-2 rounded-md hover:opacity-95 focus:outline-none focus:ring-2 focus:ring-redhalo-red/35 focus:ring-offset-2 transition-colors"
                >
                  Save Structure
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Advanced Formula Manager Modal */}
      <AdvancedFormulaManager
        isOpen={showFormulaManager}
        onClose={() => {
          setShowFormulaManager(false);
          setCurrentComponentForFormula(null);
        }}
        savedComponents={savedComponents}
        organizationalLevels={organizationalLevels}
        statutoryRates={statutoryRates}
        onFormulaSelect={handleFormulaSelect}
        selectedFormula={currentComponentForFormula ? 
          formData.components.find(comp => comp.id === currentComponentForFormula)?.selectedFormula : 
          null
        }
      />
    </div>
  );
};

export default SalaryStructureForm;